import { useState } from 'react'

export type UploadingProgress = {
  isUploading: boolean
  progressPercent: number
  totalFilesCount: number
  currentFilesCount: number
}

type UseProgress = {
  progress: UploadingProgress
  resetProgress: () => void
  setProgressIsUploading: (totalFilesCount?: number) => void
  setProgressStopUploading: () => void
  setTotalFilesCount: (totalFilesCount: number) => void
  setCurrentFilesCount: (currentFilesCount: number) => void
  setProgressPercent: (progressPercent: number) => void
  updateProgressPercent: (newState: Partial<UploadingProgress>) => void
}

const initProgressState = () => {
  return initProgress
}

const useProgress = (): UseProgress => {
  const [progress, setProgress] = useState<UploadingProgress>(initProgressState)

  const resetProgress = () => {
    setProgress(initProgress)
  }

  const setProgressIsUploading = (totalFilesCount = progress.totalFilesCount) => {
    setProgress({
      ...progress,
      totalFilesCount,
      isUploading: true,
    })
  }

  const setProgressStopUploading = () => {
    setProgress({
      ...progress,
      isUploading: false,
    })
  }

  const setTotalFilesCount: UseProgress['setTotalFilesCount'] = (totalFilesCount) => {
    setProgress({
      ...progress,
      totalFilesCount,
    })
  }
  const setCurrentFilesCount: UseProgress['setCurrentFilesCount'] = (currentFilesCount) => {
    setProgress((prevState) => {
      const progressPercent = getProgressPercent(currentFilesCount, prevState.totalFilesCount)
      return {
        ...prevState,
        currentFilesCount,
        progressPercent,
      }
    })
  }

  const setProgressPercent: UseProgress['setProgressPercent'] = (progressPercent) => {
    setProgress({
      ...progress,
      progressPercent,
    })
  }

  const updateProgressPercent: UseProgress['updateProgressPercent'] = (newState) => {
    setProgress((prevState) => ({
      ...prevState,
      ...newState,
    }))
  }

  return {
    progress,
    resetProgress,
    setProgressIsUploading,
    setProgressStopUploading,
    setTotalFilesCount,
    setCurrentFilesCount,
    setProgressPercent,
    updateProgressPercent,
  }
}

export default useProgress

const initProgress: UploadingProgress = {
  isUploading: false,
  progressPercent: 0,
  totalFilesCount: 0,
  currentFilesCount: 0,
}

const getProgressPercent = (currentFilesCount: number, totalFilesCount: number) =>
  (currentFilesCount / totalFilesCount) * 100
